import React, { useState, useCallback, useMemo } from 'react';
import {
  Activity,
  Radio,
  CreditCard,
  ShieldCheck,
  Calendar,
  Video,
  ShieldAlert,
  Share2,
  Phone,
  BarChart3,
  FileText,
  Upload,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  X,
  Copy,
  XCircle,
  Eye,
  Users,
} from 'lucide-react';
import {
  useWebhookSummary,
  useWebhookEvents,
  useWebhookStripe,
  useWebhookCheckr,
  useWebhookBookings,
  useWebhookVideoEvents,
  useWebhookGroupSessions,
  useWebhookSessionMonitoring,
  useWebhookSafetyFlags,
  useWebhookReferrals,
  useWebhookFraudSignals,
  useWebhookCallLogs,
  useWebhookAnalytics,
  useWebhookAuditLogs,
  useWebhookUploads,
} from '../hooks/useWebhooks';
import type {
  WebhookEventStatus,
  WebhookSeverity,
  WebhookBaseParams,
  WebhookStripeParams,
  WebhookCheckrParams,
  WebhookBookingParams,
  WebhookSessionParams,
  WebhookSafetyParams,
  WebhookFraudParams,
  WebhookReferralParams,
  WebhookCallLogParams,
  WebhookAnalyticsParams,
  WebhookAuditParams,
  WebhookUploadParams,
} from '../types/webhook';
import { cn, formatDateTime, formatNumber, formatDollars } from '../utils/helpers';
import { Badge } from '../components/ui';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 20;

type TabKey =
  | 'summary' | 'events' | 'stripe' | 'checkr'
  | 'bookings' | 'sessions' | 'safety' | 'referrals'
  | 'call-logs' | 'analytics' | 'audit-logs' | 'uploads';

interface TabDef { key: TabKey; label: string; icon: React.ElementType }

const TABS: TabDef[] = [
  { key: 'summary', label: 'Summary', icon: Activity },
  { key: 'events', label: 'All Events', icon: Radio },
  { key: 'stripe', label: 'Stripe', icon: CreditCard },
  { key: 'checkr', label: 'Checkr', icon: ShieldCheck },
  { key: 'bookings', label: 'Bookings', icon: Calendar },
  { key: 'sessions', label: 'Sessions', icon: Video },
  { key: 'safety', label: 'Safety', icon: ShieldAlert },
  { key: 'referrals', label: 'Referrals', icon: Share2 },
  { key: 'call-logs', label: 'Call Logs', icon: Phone },
  { key: 'analytics', label: 'Analytics', icon: BarChart3 },
  { key: 'audit-logs', label: 'Audit Logs', icon: FileText },
  { key: 'uploads', label: 'Uploads', icon: Upload },
];

// ============================================================================
// Badge helpers
// ============================================================================

function eventStatusBadge(status: WebhookEventStatus | string): { variant: 'success' | 'warning' | 'error' | 'info' | 'muted' } {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'muted'> = {
    PROCESSED: 'success', RECEIVED: 'info', FAILED: 'error', SKIPPED: 'muted',
    COMPLETED: 'success', PENDING: 'warning', PROCESSING: 'info', DELIVERED: 'success',
  };
  return { variant: map[status] || 'muted' };
}

function severityBadge(severity: WebhookSeverity | string): { variant: 'success' | 'warning' | 'error' | 'info' | 'muted' } {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'muted'> = {
    INFO: 'info', WARNING: 'warning', ERROR: 'error', CRITICAL: 'error',
  };
  return { variant: map[severity] || 'muted' };
}

function sourceBadge(source: string): { variant: 'success' | 'warning' | 'error' | 'info' | 'muted' } {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'muted'> = {
    STRIPE: 'info', CHECKR: 'warning',
  };
  return { variant: map[source] || 'muted' };
}

// ============================================================================
// Shared sub-components
// ============================================================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  loading?: boolean;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon: Icon, color, bgColor, loading, subtitle }) => (
  <div className="card p-4">
    <div className="flex items-center gap-3">
      <div className={cn('p-2 rounded-xl flex-shrink-0', bgColor)}>
        <Icon className={cn('h-5 w-5', color)} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        {loading ? (
          <div className="h-7 w-16 bg-muted/30 rounded animate-pulse mt-0.5" />
        ) : (
          <p className="text-xl font-bold text-foreground">{value}</p>
        )}
        {subtitle && !loading && <p className="text-[10px] text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  </div>
);

interface FilterOption { label: string; value: string }
interface FilterField { key: string; label: string; options: FilterOption[] }

const FilterBar: React.FC<{
  filters: FilterField[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onSearch?: (value: string) => void;
  searchValue?: string;
  searchPlaceholder?: string;
}> = ({ filters, values, onChange, onSearch, searchValue, searchPlaceholder = 'Search...' }) => (
  <div className="flex flex-wrap items-center gap-2 mb-4">
    {onSearch && (
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="text"
          value={searchValue || ''}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="pl-8 pr-3 py-1.5 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 w-48"
        />
        {searchValue && (
          <button onClick={() => onSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
            <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </div>
    )}
    {filters.map((f) => (
      <select
        key={f.key}
        value={values[f.key] || ''}
        onChange={(e) => onChange(f.key, e.target.value)}
        className="px-3 py-1.5 text-sm bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
      >
        <option value="">{f.label}</option>
        {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ))}
  </div>
);

const Pagination: React.FC<{
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}> = ({ page, totalPages, total, onPageChange, loading }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
      <p className="text-xs text-muted-foreground">Page {page} of {totalPages} ({formatNumber(total)} total)</p>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1 || loading} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronLeft className="h-4 w-4 text-foreground" />
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number;
          if (totalPages <= 5) pageNum = i + 1;
          else if (page <= 3) pageNum = i + 1;
          else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
          else pageNum = page - 2 + i;
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              className={cn('w-8 h-8 text-xs rounded-lg transition-colors', pageNum === page ? 'bg-primary/20 text-primary font-semibold' : 'text-muted-foreground hover:bg-primary/10')}
            >
              {pageNum}
            </button>
          );
        })}
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages || loading} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronRight className="h-4 w-4 text-foreground" />
        </button>
      </div>
    </div>
  );
};

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="pt-2 mt-2 first:mt-0 first:pt-0">
    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">{children}</div>
  </div>
);

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-[10px] text-muted-foreground whitespace-nowrap">{label}</span>
      <span className="text-[10px] text-foreground text-right break-all">{value}</span>
    </div>
  );
};

const CopyableId: React.FC<{ id: string | null; label?: string }> = ({ id, label }) => {
  if (!id) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground bg-muted/20 px-1.5 py-0.5 rounded cursor-pointer hover:text-foreground transition-colors"
      title={id}
      onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(id); }}
    >
      {label && <span className="text-muted-foreground/60">{label}:</span>}
      {id.length > 24 ? id.slice(0, 10) + '...' + id.slice(-6) : id}
      <Copy className="h-2.5 w-2.5" />
    </span>
  );
};

const STATUS_FILTER: FilterOption[] = [
  { label: 'Received', value: 'RECEIVED' },
  { label: 'Processed', value: 'PROCESSED' },
  { label: 'Failed', value: 'FAILED' },
  { label: 'Skipped', value: 'SKIPPED' },
];

const SEVERITY_FILTER: FilterOption[] = [
  { label: 'Info', value: 'INFO' },
  { label: 'Warning', value: 'WARNING' },
  { label: 'Error', value: 'ERROR' },
  { label: 'Critical', value: 'CRITICAL' },
];

// ============================================================================
// Summary Tab
// ============================================================================

const SummaryTab: React.FC = () => {
  const { data, loading, refetch } = useWebhookSummary();

  const sourceEntries = data?.bySource ? Object.entries(data.bySource) : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <button onClick={() => refetch()} disabled={loading} className="p-2 rounded-lg hover:bg-primary/10 transition-colors">
          <RefreshCw className={cn('h-4 w-4 text-muted-foreground', loading && 'animate-spin')} />
        </button>
      </div>

      {/* Top-level stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard label="Total Events" value={formatNumber(data?.totalEvents)} icon={Radio} color="text-blue-400" bgColor="bg-blue-400/10" loading={loading} />
        <StatCard label="Last 24 Hours" value={formatNumber(data?.last24hCount)} icon={Clock} color="text-cyan-400" bgColor="bg-cyan-400/10" loading={loading} />
        <StatCard label="Recent Failures" value={formatNumber(data?.recentFailures)} icon={AlertTriangle} color="text-red-400" bgColor="bg-red-400/10" loading={loading} />
      </div>

      {/* Status breakdown */}
      {data?.byStatus && (
        <div className="card p-4">
          <h4 className="text-xs font-semibold text-muted-foreground mb-3">By Status</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(data.byStatus).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between p-2 bg-muted/10 rounded-lg">
                <Badge variant={eventStatusBadge(status).variant} size="sm">{status}</Badge>
                <span className="text-sm font-semibold text-foreground tabular-nums">{formatNumber(count)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-source breakdown */}
      {sourceEntries.length > 0 && (
        <div className="card p-4">
          <h4 className="text-xs font-semibold text-muted-foreground mb-3">By Source</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sourceEntries.map(([source, stats]) => (
              <div key={source} className="p-3 bg-muted/10 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant={sourceBadge(source).variant} size="sm">{source}</Badge>
                  <span className="text-sm font-bold text-foreground tabular-nums">{formatNumber(stats.total)}</span>
                </div>
                <div className="grid grid-cols-3 gap-1 text-[10px]">
                  <div className="text-center">
                    <p className="text-emerald-400 font-semibold">{formatNumber(stats.processed)}</p>
                    <p className="text-muted-foreground">Processed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-red-400 font-semibold">{formatNumber(stats.failed)}</p>
                    <p className="text-muted-foreground">Failed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-yellow-400 font-semibold">{formatNumber(stats.pending)}</p>
                    <p className="text-muted-foreground">Pending</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Severity breakdown */}
      {data?.bySeverity && Object.keys(data.bySeverity).length > 0 && (
        <div className="card p-4">
          <h4 className="text-xs font-semibold text-muted-foreground mb-3">By Severity</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(data.bySeverity).map(([sev, count]) => (
              <div key={sev} className="flex items-center justify-between p-2 bg-muted/10 rounded-lg">
                <Badge variant={severityBadge(sev).variant} size="sm">{sev}</Badge>
                <span className="text-sm font-semibold text-foreground tabular-nums">{formatNumber(count)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// All Events Tab (Unified Feed)
// ============================================================================

const AllEventsTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<WebhookBaseParams>(() => ({
    status: filters.status || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useWebhookEvents(params);

  const handleFilter = useCallback((key: string, value: string) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); }, []);

  return (
    <div>
      <FilterBar
        filters={[{ key: 'status', label: 'All Statuses', options: STATUS_FILTER }]}
        values={filters}
        onChange={handleFilter}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchValue={search}
        searchPlaceholder="Search events..."
      />
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No events found</div>
      ) : (
        <div className="space-y-2">
          {items.map((evt) => {
            const isExp = expandedId === evt.id;
            return (
              <div key={evt.id} className={cn('card p-3 cursor-pointer transition-all', isExp && 'ring-1 ring-primary/30')} onClick={() => setExpandedId(isExp ? null : evt.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">{new Date(evt.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    <Badge variant={sourceBadge(evt.source).variant} size="sm">{evt.source}</Badge>
                    <span className="text-xs text-foreground font-mono truncate">{evt.eventType}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={eventStatusBadge(evt.status).variant} size="sm">{evt.status}</Badge>
                    <CopyableId id={evt.externalId} />
                  </div>
                </div>
                {evt.errorMessage && <p className="text-[10px] text-red-400 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{evt.errorMessage}</p>}
                {isExp && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Event">
                      <DetailRow label="External ID" value={evt.externalId} />
                      <DetailRow label="Type" value={evt.eventType} />
                      <DetailRow label="Source" value={evt.source} />
                      <DetailRow label="Status" value={evt.status} />
                    </DetailSection>
                    {evt.errorMessage && <DetailSection title="Error"><div className="col-span-full text-[10px] text-red-400">{evt.errorMessage}</div></DetailSection>}
                    <DetailSection title="Timeline">
                      <DetailRow label="Created" value={formatDateTime(evt.createdAt)} />
                      {evt.processedAt && <DetailRow label="Processed" value={formatDateTime(evt.processedAt)} />}
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Stripe Tab
// ============================================================================

const StripeTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<WebhookStripeParams>(() => ({
    eventType: filters.eventType || undefined,
    status: filters.status || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useWebhookStripe(params);

  const handleFilter = useCallback((key: string, value: string) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); }, []);

  const filterFields: FilterField[] = [
    { key: 'status', label: 'All Statuses', options: STATUS_FILTER },
    { key: 'eventType', label: 'All Types', options: [
      { label: 'payment_intent.succeeded', value: 'payment_intent.succeeded' },
      { label: 'payment_intent.failed', value: 'payment_intent.failed' },
      { label: 'charge.succeeded', value: 'charge.succeeded' },
      { label: 'charge.refunded', value: 'charge.refunded' },
      { label: 'transfer.created', value: 'transfer.created' },
      { label: 'payout.paid', value: 'payout.paid' },
      { label: 'payout.failed', value: 'payout.failed' },
    ]},
  ];

  return (
    <div>
      <FilterBar filters={filterFields} values={filters} onChange={handleFilter} onSearch={(v) => { setSearch(v); setPage(1); }} searchValue={search} searchPlaceholder="Search Stripe events..." />
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No Stripe events found</div>
      ) : (
        <div className="space-y-2">
          {items.map((evt) => {
            const isExp = expandedId === evt.id;
            return (
              <div key={evt.id} className={cn('card p-3 cursor-pointer transition-all', isExp && 'ring-1 ring-primary/30')} onClick={() => setExpandedId(isExp ? null : evt.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">{new Date(evt.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    <span className="text-xs text-foreground font-mono truncate">{evt.eventType}</span>
                    <Badge variant={eventStatusBadge(evt.status).variant} size="sm">{evt.status}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {evt.retryCount > 0 && <span className={cn('text-xs tabular-nums', evt.retryCount > 3 ? 'text-red-400 font-semibold' : 'text-muted-foreground')}>{evt.retryCount} retries</span>}
                    <CopyableId id={evt.stripeEventId} />
                    {evt.livemode && <Badge variant="success" size="sm">LIVE</Badge>}
                    {!evt.livemode && <Badge variant="muted" size="sm">TEST</Badge>}
                  </div>
                </div>
                {evt.errorMessage && <p className="text-[10px] text-red-400 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{evt.errorMessage}</p>}
                {isExp && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Event">
                      <DetailRow label="Stripe Event ID" value={evt.stripeEventId} />
                      <DetailRow label="Type" value={evt.eventType} />
                      <DetailRow label="Status" value={evt.status} />
                      <DetailRow label="Live Mode" value={evt.livemode ? 'Yes' : 'No'} />
                      <DetailRow label="API Version" value={evt.apiVersion} />
                      <DetailRow label="Retry Count" value={String(evt.retryCount)} />
                    </DetailSection>
                    {(evt.relatedObjectId || evt.relatedObjectType) && (
                      <DetailSection title="Related Object">
                        <DetailRow label="Type" value={evt.relatedObjectType} />
                        <DetailRow label="ID" value={evt.relatedObjectId} />
                      </DetailSection>
                    )}
                    {evt.errorMessage && <DetailSection title="Error"><div className="col-span-full text-[10px] text-red-400">{evt.errorMessage}</div></DetailSection>}
                    <DetailSection title="Timeline">
                      <DetailRow label="Created" value={formatDateTime(evt.createdAt)} />
                      {evt.processedAt && <DetailRow label="Processed" value={formatDateTime(evt.processedAt)} />}
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Checkr Tab
// ============================================================================

// Helper to extract display-friendly fields from the nested Checkr webhook payload
function parseCheckrPayload(evt: { type: string; objectType: string; payload: Record<string, unknown> }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const obj = (evt.payload?.data as any)?.object as Record<string, unknown> | undefined;
  if (!obj) return { candidateId: null, reportId: null, reportStatus: null, result: null, userName: null, customId: null, package_: null, invitationUrl: null };

  const objectType = evt.objectType;
  let candidateId: string | null = null;
  let reportId: string | null = null;
  let reportStatus: string | null = null;
  let result: string | null = null;
  let userName: string | null = null;
  let customId: string | null = null;
  let package_: string | null = null;
  let invitationUrl: string | null = null;

  if (objectType === 'report') {
    reportId = (obj.id as string) ?? null;
    candidateId = (obj.candidate_id as string) ?? null;
    reportStatus = (obj.status as string) ?? null;
    result = (obj.result as string) ?? null;
    package_ = (obj.package as string) ?? null;
  } else if (objectType === 'candidate') {
    candidateId = (obj.id as string) ?? null;
    const first = obj.first_name as string | null;
    const last = obj.last_name as string | null;
    userName = [first, last].filter(Boolean).join(' ') || null;
    customId = (obj.custom_id as string) ?? null;
  } else if (objectType === 'invitation') {
    candidateId = (obj.candidate_id as string) ?? null;
    reportId = (obj.report_id as string) ?? null;
    package_ = (obj.package as string) ?? null;
    invitationUrl = (obj.invitation_url as string) ?? null;
  }

  return { candidateId, reportId, reportStatus, result, userName, customId, package_, invitationUrl };
}

const CheckrTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<WebhookCheckrParams>(() => ({
    eventType: filters.eventType || undefined,
    status: filters.status || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useWebhookCheckr(params);

  const handleFilter = useCallback((key: string, value: string) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); }, []);

  const filterFields: FilterField[] = [
    { key: 'status', label: 'All Statuses', options: STATUS_FILTER },
    { key: 'eventType', label: 'All Types', options: [
      { label: 'report.completed', value: 'report.completed' },
      { label: 'report.created', value: 'report.created' },
      { label: 'report.updated', value: 'report.updated' },
      { label: 'invitation.created', value: 'invitation.created' },
      { label: 'invitation.completed', value: 'invitation.completed' },
      { label: 'candidate.created', value: 'candidate.created' },
      { label: 'candidate.updated', value: 'candidate.updated' },
      { label: 'package.created', value: 'package.created' },
      { label: 'package.updated', value: 'package.updated' },
    ]},
  ];

  return (
    <div>
      <FilterBar filters={filterFields} values={filters} onChange={handleFilter} onSearch={(v) => { setSearch(v); setPage(1); }} searchValue={search} searchPlaceholder="Search Checkr events..." />
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No Checkr events found</div>
      ) : (
        <div className="space-y-2">
          {items.map((evt) => {
            const isExp = expandedId === evt.id;
            const parsed = parseCheckrPayload(evt);
            return (
              <div key={evt.id} className={cn('card p-3 cursor-pointer transition-all', isExp && 'ring-1 ring-primary/30')} onClick={() => setExpandedId(isExp ? null : evt.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">{new Date(evt.receivedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    <span className="text-xs text-foreground font-mono truncate">{evt.type}</span>
                    <Badge variant={eventStatusBadge(evt.status).variant} size="sm">{evt.status}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {parsed.reportStatus && <Badge variant="muted" size="sm">{parsed.reportStatus}</Badge>}
                    {parsed.result && <Badge variant={parsed.result === 'clear' ? 'success' : 'warning'} size="sm">{parsed.result}</Badge>}
                    {parsed.userName && <span className="text-xs text-muted-foreground">{parsed.userName}</span>}
                    <CopyableId id={evt.checkrEventId} />
                  </div>
                </div>
                {evt.error && <p className="text-[10px] text-red-400 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{evt.error}</p>}
                {isExp && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Event">
                      <DetailRow label="Checkr Event ID" value={evt.checkrEventId} />
                      <DetailRow label="Type" value={evt.type} />
                      <DetailRow label="Object Type" value={evt.objectType} />
                      <DetailRow label="Object ID" value={evt.objectId} />
                      <DetailRow label="Status" value={evt.status} />
                    </DetailSection>
                    <DetailSection title="Subject">
                      <DetailRow label="Candidate ID" value={parsed.candidateId} />
                      <DetailRow label="Report ID" value={parsed.reportId} />
                      <DetailRow label="Report Status" value={parsed.reportStatus} />
                      <DetailRow label="Result" value={parsed.result} />
                      <DetailRow label="Package" value={parsed.package_} />
                      <DetailRow label="User Name" value={parsed.userName} />
                      <DetailRow label="Custom ID" value={parsed.customId} />
                      <DetailRow label="Invitation URL" value={parsed.invitationUrl} />
                    </DetailSection>
                    {evt.error && <DetailSection title="Error"><div className="col-span-full text-[10px] text-red-400">{evt.error}</div></DetailSection>}
                    <DetailSection title="Timeline">
                      <DetailRow label="Received" value={formatDateTime(evt.receivedAt)} />
                      {evt.processedAt && <DetailRow label="Processed" value={formatDateTime(evt.processedAt)} />}
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Bookings Tab
// ============================================================================

const BookingsTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<WebhookBookingParams>(() => ({
    eventType: filters.eventType || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useWebhookBookings(params);

  const handleFilter = useCallback((key: string, value: string) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); }, []);

  const filterFields: FilterField[] = [
    { key: 'eventType', label: 'All Types', options: [
      { label: 'Created', value: 'CREATED' },
      { label: 'Confirmed', value: 'CONFIRMED' },
      { label: 'Started', value: 'STARTED' },
      { label: 'Completed', value: 'COMPLETED' },
      { label: 'Cancelled', value: 'CANCELLED' },
      { label: 'No Show', value: 'NO_SHOW' },
    ]},
  ];

  return (
    <div>
      <FilterBar filters={filterFields} values={filters} onChange={handleFilter} onSearch={(v) => { setSearch(v); setPage(1); }} searchValue={search} searchPlaceholder="Search booking events..." />
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No booking events found</div>
      ) : (
        <div className="space-y-2">
          {items.map((evt) => {
            const isExp = expandedId === evt.id;
            return (
              <div key={evt.id} className={cn('card p-3 cursor-pointer transition-all', isExp && 'ring-1 ring-primary/30')} onClick={() => setExpandedId(isExp ? null : evt.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">{new Date(evt.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    <span className="text-xs text-primary">Booking #{evt.bookingId}</span>
                    <Badge variant="info" size="sm">{evt.eventType.replace(/_/g, ' ')}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {evt.previousStatus && evt.newStatus && (
                      <span className="text-[10px] text-muted-foreground">{evt.previousStatus} → {evt.newStatus}</span>
                    )}
                    {evt.userName && <span className="text-xs text-muted-foreground">{evt.userName}</span>}
                  </div>
                </div>
                {isExp && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Event">
                      <DetailRow label="Booking ID" value={`#${evt.bookingId}`} />
                      <DetailRow label="Event Type" value={evt.eventType} />
                      <DetailRow label="Previous Status" value={evt.previousStatus} />
                      <DetailRow label="New Status" value={evt.newStatus} />
                      <DetailRow label="Triggered By" value={evt.triggeredBy} />
                    </DetailSection>
                    {(evt.userId != null || evt.userName) && (
                      <DetailSection title="User">
                        {evt.userId != null && <DetailRow label="User ID" value={String(evt.userId)} />}
                        <DetailRow label="Name" value={evt.userName} />
                      </DetailSection>
                    )}
                    <DetailSection title="Timeline">
                      <DetailRow label="Created" value={formatDateTime(evt.createdAt)} />
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Sessions Tab (Video / Group / Monitoring toggle)
// ============================================================================

type SessionSource = 'video' | 'group' | 'monitoring';

const SessionsTab: React.FC = () => {
  const [source, setSource] = useState<SessionSource>('video');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<WebhookSessionParams>(() => ({
    eventType: filters.eventType || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, page]);

  const video = useWebhookVideoEvents(source === 'video' ? params : { limit: 0 });
  const group = useWebhookGroupSessions(source === 'group' ? params : { limit: 0 });
  const monitor = useWebhookSessionMonitoring(source === 'monitoring' ? params : { limit: 0 });

  const active = source === 'video' ? video : source === 'group' ? group : monitor;

  const handleFilter = useCallback((key: string, value: string) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); }, []);

  const handleSourceChange = (s: SessionSource) => { setSource(s); setPage(1); setExpandedId(null); setFilters({}); };

  return (
    <div>
      {/* Source toggle */}
      <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1 mb-4 w-fit">
        {([['video', 'Video', Video], ['group', 'Group', Users], ['monitoring', 'Monitoring', Eye]] as const).map(([key, label, Icon]) => (
          <button key={key} onClick={() => handleSourceChange(key)} className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors', source === key ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:text-foreground')}>
            <Icon className="h-3.5 w-3.5" />{label}
          </button>
        ))}
      </div>

      <FilterBar filters={[]} values={filters} onChange={handleFilter} />

      {active.loading && active.items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : active.items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No session events found</div>
      ) : (
        <div className="space-y-2">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {active.items.map((item: any) => {
            const isExp = expandedId === item.id;
            return (
              <div key={item.id} className={cn('card p-3 cursor-pointer transition-all', isExp && 'ring-1 ring-primary/30')} onClick={() => setExpandedId(isExp ? null : item.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">{new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    <span className="text-xs text-primary">Session #{item.sessionId}</span>
                    <Badge variant="info" size="sm">{item.eventType?.replace(/_/g, ' ') || 'EVENT'}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.userName && <span className="text-xs text-muted-foreground">{item.userName}</span>}
                    {item.participantName && <span className="text-xs text-muted-foreground">{item.participantName}</span>}
                    {item.flagged && <Badge variant="error" size="sm">Flagged</Badge>}
                    {item.severity && <Badge variant={severityBadge(item.severity).variant} size="sm">{item.severity}</Badge>}
                  </div>
                </div>
                {item.reason && <p className="text-[10px] text-muted-foreground mt-1">{item.reason}</p>}
                {isExp && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Details">
                      <DetailRow label="Session ID" value={String(item.sessionId)} />
                      <DetailRow label="Event Type" value={item.eventType} />
                      {item.userId != null && <DetailRow label="User ID" value={String(item.userId)} />}
                      <DetailRow label="User" value={item.userName || item.participantName} />
                      {item.roomName && <DetailRow label="Room" value={item.roomName} />}
                      {item.duration != null && <DetailRow label="Duration" value={`${item.duration}s`} />}
                      {item.participantCount != null && <DetailRow label="Participants" value={String(item.participantCount)} />}
                      {item.monitorType && <DetailRow label="Monitor Type" value={item.monitorType} />}
                      {item.status && <DetailRow label="Status" value={item.status} />}
                      <DetailRow label="Reason" value={item.reason} />
                    </DetailSection>
                    <DetailSection title="Timeline">
                      <DetailRow label="Created" value={formatDateTime(item.createdAt)} />
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={active.page} totalPages={active.totalPages} total={active.total} onPageChange={setPage} loading={active.loading} />
    </div>
  );
};

// ============================================================================
// Safety Tab (Safety Flags / Fraud Signals toggle)
// ============================================================================

type SafetySource = 'flags' | 'fraud';

const SafetyTab: React.FC = () => {
  const [source, setSource] = useState<SafetySource>('flags');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const safetyParams = useMemo<WebhookSafetyParams>(() => ({
    severity: filters.severity || undefined,
    flagType: filters.flagType || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const fraudParams = useMemo<WebhookFraudParams>(() => ({
    severity: filters.severity || undefined,
    signalType: filters.signalType || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const flags = useWebhookSafetyFlags(source === 'flags' ? safetyParams : { limit: 0 });
  const fraud = useWebhookFraudSignals(source === 'fraud' ? fraudParams : { limit: 0 });

  const active = source === 'flags' ? flags : fraud;

  const handleFilter = useCallback((key: string, value: string) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); }, []);

  const handleSourceChange = (s: SafetySource) => { setSource(s); setPage(1); setExpandedId(null); setFilters({}); setSearch(''); };

  return (
    <div>
      <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1 mb-4 w-fit">
        <button onClick={() => handleSourceChange('flags')} className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors', source === 'flags' ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:text-foreground')}>
          <ShieldAlert className="h-3.5 w-3.5" />Safety Flags
        </button>
        <button onClick={() => handleSourceChange('fraud')} className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors', source === 'fraud' ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:text-foreground')}>
          <AlertTriangle className="h-3.5 w-3.5" />Fraud Signals
        </button>
      </div>

      <FilterBar
        filters={[{ key: 'severity', label: 'All Severities', options: SEVERITY_FILTER }]}
        values={filters}
        onChange={handleFilter}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchValue={search}
        searchPlaceholder={source === 'flags' ? 'Search safety flags...' : 'Search fraud signals...'}
      />

      {active.loading && active.items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : active.items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No {source === 'flags' ? 'safety flags' : 'fraud signals'} found</div>
      ) : (
        <div className="space-y-2">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {active.items.map((item: any) => {
            const isExp = expandedId === item.id;
            return (
              <div key={item.id} className={cn('card p-3 cursor-pointer transition-all', isExp && 'ring-1 ring-primary/30')} onClick={() => setExpandedId(isExp ? null : item.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">{new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    <Badge variant={severityBadge(item.severity).variant} size="sm">{item.severity}</Badge>
                    <span className="text-xs text-foreground font-mono truncate">{item.flagType || item.signalType}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.userName && <span className="text-xs text-muted-foreground">{item.userName}</span>}
                    {item.resolved === true && <Badge variant="success" size="sm">Resolved</Badge>}
                    {item.resolved === false && <Badge variant="warning" size="sm">Open</Badge>}
                    {item.riskScore != null && <span className="text-xs text-red-400 font-semibold tabular-nums">Risk: {item.riskScore}</span>}
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1 truncate">{item.description}</p>
                {isExp && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Details">
                      <DetailRow label="Type" value={item.flagType || item.signalType} />
                      <DetailRow label="Severity" value={item.severity} />
                      <DetailRow label="Description" value={item.description} />
                      {item.aiConfidence != null && <DetailRow label="AI Confidence" value={`${(item.aiConfidence * 100).toFixed(1)}%`} />}
                      {item.riskScore != null && <DetailRow label="Risk Score" value={String(item.riskScore)} />}
                      {item.actionTaken && <DetailRow label="Action Taken" value={item.actionTaken} />}
                    </DetailSection>
                    <DetailSection title="Subject">
                      {item.userId != null && <DetailRow label="User ID" value={String(item.userId)} />}
                      <DetailRow label="User" value={item.userName} />
                      {item.sessionId != null && <DetailRow label="Session ID" value={String(item.sessionId)} />}
                      {item.referralId != null && <DetailRow label="Referral ID" value={String(item.referralId)} />}
                    </DetailSection>
                    {(item.resolved != null) && (
                      <DetailSection title="Resolution">
                        <DetailRow label="Resolved" value={item.resolved ? 'Yes' : 'No'} />
                        <DetailRow label="Resolved By" value={item.resolvedBy} />
                        {item.resolvedAt && <DetailRow label="Resolved At" value={formatDateTime(item.resolvedAt)} />}
                      </DetailSection>
                    )}
                    <DetailSection title="Timeline">
                      <DetailRow label="Created" value={formatDateTime(item.createdAt)} />
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={active.page} totalPages={active.totalPages} total={active.total} onPageChange={setPage} loading={active.loading} />
    </div>
  );
};

// ============================================================================
// Referrals Tab
// ============================================================================

const ReferralsTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<WebhookReferralParams>(() => ({
    eventType: filters.eventType || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useWebhookReferrals(params);

  const handleFilter = useCallback((key: string, value: string) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); }, []);

  return (
    <div>
      <FilterBar
        filters={[{ key: 'eventType', label: 'All Types', options: [
          { label: 'booking.completed', value: 'booking.completed' },
        ]}]}
        values={filters} onChange={handleFilter} onSearch={(v) => { setSearch(v); setPage(1); }} searchValue={search} searchPlaceholder="Search referrals..."
      />
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No referral events found</div>
      ) : (
        <div className="space-y-2">
          {items.map((evt) => {
            const isExp = expandedId === evt.id;
            return (
              <div key={evt.id} className={cn('card p-3 cursor-pointer transition-all', isExp && 'ring-1 ring-primary/30')} onClick={() => setExpandedId(isExp ? null : evt.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">{evt.processedAt ? new Date(evt.processedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}</div>
                    <Badge variant="info" size="sm">{evt.eventType?.replace(/[._]/g, ' ') ?? 'unknown'}</Badge>
                    {evt.bookingId != null && <span className="text-xs text-muted-foreground">Booking #{evt.bookingId}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {evt.resultStatus && <Badge variant={eventStatusBadge(evt.resultStatus).variant} size="sm">{evt.resultStatus}</Badge>}
                    {evt.referralId != null && <span className="text-xs text-primary">Referral #{evt.referralId}</span>}
                  </div>
                </div>
                {evt.resultMessage && <p className="text-[10px] text-muted-foreground mt-1">{evt.resultMessage}</p>}
                {isExp && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Event">
                      <DetailRow label="Event Type" value={evt.eventType} />
                      <DetailRow label="Result Status" value={evt.resultStatus} />
                      <DetailRow label="Result Message" value={evt.resultMessage} />
                      {evt.bookingId != null && <DetailRow label="Booking ID" value={String(evt.bookingId)} />}
                      {evt.referralId != null && <DetailRow label="Referral ID" value={String(evt.referralId)} />}
                    </DetailSection>
                    {evt.referral && (
                      <DetailSection title="Referral">
                        <DetailRow label="Referral ID" value={String(evt.referral.id)} />
                        {evt.referral.referralCode && <DetailRow label="Code" value={String(evt.referral.referralCode)} />}
                        {evt.referral.status && <DetailRow label="Status" value={String(evt.referral.status)} />}
                        {evt.referral.referrerUserId != null && <DetailRow label="Referrer User ID" value={String(evt.referral.referrerUserId)} />}
                        {evt.referral.refereeUserId != null && <DetailRow label="Referee User ID" value={String(evt.referral.refereeUserId)} />}
                      </DetailSection>
                    )}
                    <DetailSection title="Timeline">
                      <DetailRow label="Processed" value={evt.processedAt ? formatDateTime(evt.processedAt) : undefined} />
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Call Logs Tab
// ============================================================================

const CallLogsTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<WebhookCallLogParams>(() => ({
    status: filters.status || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useWebhookCallLogs(params);

  const handleFilter = useCallback((key: string, value: string) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); }, []);

  return (
    <div>
      <FilterBar
        filters={[
          { key: 'status', label: 'All Statuses', options: [{ label: 'Success', value: 'success' }, { label: 'Failed', value: 'failed' }] },
        ]}
        values={filters} onChange={handleFilter} onSearch={(v) => { setSearch(v); setPage(1); }} searchValue={search} searchPlaceholder="Search call logs..."
      />
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No call logs found</div>
      ) : (
        <div className="space-y-2">
          {items.map((evt) => {
            const isExp = expandedId === evt.id;
            return (
              <div key={evt.id} className={cn('card p-3 cursor-pointer transition-all', isExp && 'ring-1 ring-primary/30')} onClick={() => setExpandedId(isExp ? null : evt.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">{evt.sentAt ? new Date(evt.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}</div>
                    <Badge variant={evt.type === 'INCOMING_CALL' ? 'info' : 'default'} size="sm">{evt.type?.replace(/_/g, ' ') ?? 'unknown'}</Badge>
                    {evt.platform && <Badge variant="muted" size="sm">{evt.platform}</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={evt.status === 'success' ? 'success' : 'error'} size="sm">{evt.status}</Badge>
                    {evt.toUser?.name && <span className="text-xs text-muted-foreground">{evt.toUser.name}</span>}
                  </div>
                </div>
                {evt.error && <p className="text-[10px] text-red-400 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{evt.error}</p>}
                {isExp && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Push Notification">
                      {evt.callId != null && <DetailRow label="Call ID" value={String(evt.callId)} />}
                      <DetailRow label="Type" value={evt.type} />
                      <DetailRow label="Platform" value={evt.platform} />
                      <DetailRow label="Status" value={evt.status} />
                      <DetailRow label="Provider Message ID" value={evt.providerMessageId} />
                      <DetailRow label="Nonce" value={evt.nonce} />
                    </DetailSection>
                    {(evt.toUserId != null || evt.toUser) && (
                      <DetailSection title="Recipient">
                        {evt.toUserId != null && <DetailRow label="User ID" value={String(evt.toUserId)} />}
                        {evt.toUser?.name && <DetailRow label="Name" value={evt.toUser.name} />}
                      </DetailSection>
                    )}
                    {evt.error && <DetailSection title="Error"><div className="col-span-full text-[10px] text-red-400">{evt.error}</div></DetailSection>}
                    <DetailSection title="Timeline">
                      <DetailRow label="Sent" value={evt.sentAt ? formatDateTime(evt.sentAt) : undefined} />
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Analytics Tab
// ============================================================================

const AnalyticsTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const params = useMemo<WebhookAnalyticsParams>(() => ({
    eventName: filters.eventName || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useWebhookAnalytics(params);

  const handleFilter = useCallback((key: string, value: string) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); }, []);

  return (
    <div>
      <FilterBar
        filters={[{ key: 'eventName', label: 'All Events', options: [
          { label: 'Tab Changed', value: 'client.tab_changed' },
          { label: 'Screen Viewed', value: 'client.screen_viewed' },
          { label: 'Content Opened', value: 'client.content_auto_opened' },
        ]}]}
        values={filters} onChange={handleFilter} onSearch={(v) => { setSearch(v); setPage(1); }} searchValue={search} searchPlaceholder="Search analytics..."
      />
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No analytics events found</div>
      ) : (
        <div className="space-y-2">
          {items.map((evt) => {
            const isExp = expandedId === evt.id;
            return (
              <div key={evt.id} className={cn('card p-3 cursor-pointer transition-all', isExp && 'ring-1 ring-primary/30')} onClick={() => setExpandedId(isExp ? null : evt.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">{evt.timestamp ? new Date(evt.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}</div>
                    <Badge variant="info" size="sm">{evt.name?.replace(/\./g, ' ') ?? 'unknown'}</Badge>
                    {evt.source && <Badge variant="muted" size="sm">{evt.source}</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    {evt.sourceVersion && <span className="text-[10px] font-mono text-muted-foreground/60">v{evt.sourceVersion}</span>}
                    {evt.userId != null && <span className="text-[10px] text-muted-foreground">User #{evt.userId}</span>}
                  </div>
                </div>
                {evt.properties && Object.keys(evt.properties).length > 0 && (
                  <div className="flex items-center gap-2 mt-1">
                    {Object.entries(evt.properties).map(([k, v]) => (
                      <span key={k} className="text-[10px] text-muted-foreground">{k}: <span className="text-foreground">{String(v)}</span></span>
                    ))}
                  </div>
                )}
                {isExp && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Event">
                      <DetailRow label="Event Name" value={evt.name} />
                      <DetailRow label="Source" value={evt.source} />
                      <DetailRow label="Source Version" value={evt.sourceVersion} />
                      <DetailRow label="Session ID" value={evt.sessionId} />
                      <DetailRow label="Device ID" value={evt.deviceId} />
                      {evt.userId != null && <DetailRow label="User ID" value={String(evt.userId)} />}
                      <DetailRow label="Event ID" value={evt.eventId} />
                    </DetailSection>
                    {evt.properties && Object.keys(evt.properties).length > 0 && (
                      <DetailSection title="Properties">
                        {Object.entries(evt.properties).map(([k, v]) => (
                          <DetailRow key={k} label={k} value={String(v)} />
                        ))}
                      </DetailSection>
                    )}
                    <DetailSection title="Timeline">
                      <DetailRow label="Server Time" value={evt.timestamp ? formatDateTime(evt.timestamp) : undefined} />
                      <DetailRow label="Client Time" value={evt.clientTime ? formatDateTime(evt.clientTime) : undefined} />
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Audit Logs Tab
// ============================================================================

const AuditLogsTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<WebhookAuditParams>(() => ({
    category: filters.category || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useWebhookAuditLogs(params);

  const handleFilter = useCallback((key: string, value: string) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); }, []);

  // Parse metadata: can be a JSON string or an object
  const parseMetadata = (meta: string | Record<string, unknown> | null): Record<string, unknown> | null => {
    if (!meta) return null;
    if (typeof meta === 'string') {
      try { return JSON.parse(meta); } catch { return null; }
    }
    return meta;
  };

  return (
    <div>
      <FilterBar
        filters={[
          { key: 'category', label: 'All Types', options: [{ label: 'Audit', value: 'audit' }, { label: 'Security', value: 'security' }, { label: 'Encryption', value: 'encryption' }] },
        ]}
        values={filters} onChange={handleFilter} onSearch={(v) => { setSearch(v); setPage(1); }} searchValue={search} searchPlaceholder="Search audit logs..."
      />
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No audit logs found</div>
      ) : (
        <div className="space-y-2">
          {items.map((evt) => {
            const isExp = expandedId === evt.id;
            const meta = parseMetadata(evt.metadata);
            const ipAddress = (evt.extra as Record<string, unknown>)?.ipAddress as string | undefined;
            return (
              <div key={`${evt.logType}-${evt.id}`} className={cn('card p-3 cursor-pointer transition-all', isExp && 'ring-1 ring-primary/30')} onClick={() => setExpandedId(isExp ? null : evt.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">{evt.timestamp ? new Date(evt.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}</div>
                    <Badge variant={evt.logType === 'security' ? 'error' : evt.logType === 'encryption' ? 'warning' : 'muted'} size="sm">{evt.logType}</Badge>
                    <span className="text-xs text-foreground truncate">{evt.event}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {evt.success != null && <Badge variant={evt.success ? 'success' : 'error'} size="sm">{evt.success ? 'OK' : 'FAIL'}</Badge>}
                    {evt.userId != null && <span className="text-[10px] text-muted-foreground">User #{evt.userId}</span>}
                    {ipAddress && <span className="text-[10px] font-mono text-muted-foreground/60">{ipAddress}</span>}
                  </div>
                </div>
                {isExp && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Audit Entry">
                      <DetailRow label="Event" value={evt.event} />
                      <DetailRow label="Log Type" value={evt.logType} />
                      {evt.severity && <DetailRow label="Severity" value={evt.severity} />}
                      {evt.success != null && <DetailRow label="Success" value={evt.success ? 'Yes' : 'No'} />}
                    </DetailSection>
                    <DetailSection title="Actor">
                      {evt.userId != null && <DetailRow label="User ID" value={String(evt.userId)} />}
                      {ipAddress && <DetailRow label="IP Address" value={ipAddress} />}
                    </DetailSection>
                    {meta && Object.keys(meta).length > 0 && (
                      <DetailSection title="Metadata">
                        {Object.entries(meta).map(([k, v]) => (
                          <DetailRow key={k} label={k} value={String(v)} />
                        ))}
                      </DetailSection>
                    )}
                    {evt.extra && Object.keys(evt.extra).length > 0 && (
                      <DetailSection title="Extra">
                        {Object.entries(evt.extra).map(([k, v]) => v != null ? (
                          <DetailRow key={k} label={k} value={String(v)} />
                        ) : null)}
                      </DetailSection>
                    )}
                    <DetailSection title="Timeline">
                      <DetailRow label="Timestamp" value={evt.timestamp ? formatDateTime(evt.timestamp) : undefined} />
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Uploads Tab
// ============================================================================

const UploadsTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<WebhookUploadParams>(() => ({
    eventType: filters.eventType || undefined,
    status: filters.status || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useWebhookUploads(params);

  const handleFilter = useCallback((key: string, value: string) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); }, []);

  return (
    <div>
      <FilterBar
        filters={[
          { key: 'eventType', label: 'All Types', options: [
            { label: 'Upload Started', value: 'UPLOAD_STARTED' }, { label: 'Scan Complete', value: 'SCAN_COMPLETE' },
            { label: 'Processing', value: 'PROCESSING' }, { label: 'Complete', value: 'COMPLETE' }, { label: 'Failed', value: 'FAILED' },
          ]},
          { key: 'status', label: 'All Statuses', options: STATUS_FILTER },
        ]}
        values={filters} onChange={handleFilter} onSearch={(v) => { setSearch(v); setPage(1); }} searchValue={search} searchPlaceholder="Search uploads..."
      />
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No upload events found</div>
      ) : (
        <div className="space-y-2">
          {items.map((evt) => {
            const isExp = expandedId === evt.id;
            const sizeStr = evt.fileSize != null ? (evt.fileSize > 1024 * 1024 ? `${(evt.fileSize / (1024 * 1024)).toFixed(1)} MB` : `${(evt.fileSize / 1024).toFixed(0)} KB`) : null;
            return (
              <div key={evt.id} className={cn('card p-3 cursor-pointer transition-all', isExp && 'ring-1 ring-primary/30')} onClick={() => setExpandedId(isExp ? null : evt.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">{new Date(evt.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    <Badge variant="info" size="sm">{evt.eventType.replace(/_/g, ' ')}</Badge>
                    <Badge variant={eventStatusBadge(evt.status).variant} size="sm">{evt.status}</Badge>
                    {evt.fileName && <span className="text-xs text-foreground truncate">{evt.fileName}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {evt.fileType && <Badge variant="muted" size="sm">{evt.fileType}</Badge>}
                    {sizeStr && <span className="text-[10px] text-muted-foreground tabular-nums">{sizeStr}</span>}
                    {evt.userName && <span className="text-xs text-muted-foreground">{evt.userName}</span>}
                  </div>
                </div>
                {evt.scanResult && <p className="text-[10px] text-muted-foreground mt-1">Scan: {evt.scanResult}</p>}
                {evt.errorMessage && <p className="text-[10px] text-red-400 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{evt.errorMessage}</p>}
                {isExp && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Upload">
                      <DetailRow label="Upload ID" value={evt.uploadId} />
                      <DetailRow label="Event Type" value={evt.eventType} />
                      <DetailRow label="Status" value={evt.status} />
                      <DetailRow label="File Name" value={evt.fileName} />
                      <DetailRow label="File Type" value={evt.fileType} />
                      {sizeStr && <DetailRow label="File Size" value={sizeStr} />}
                      <DetailRow label="Scan Result" value={evt.scanResult} />
                    </DetailSection>
                    {(evt.userId != null || evt.userName) && (
                      <DetailSection title="User">
                        {evt.userId != null && <DetailRow label="User ID" value={String(evt.userId)} />}
                        <DetailRow label="Name" value={evt.userName} />
                      </DetailSection>
                    )}
                    {evt.errorMessage && <DetailSection title="Error"><div className="col-span-full text-[10px] text-red-400">{evt.errorMessage}</div></DetailSection>}
                    <DetailSection title="Timeline">
                      <DetailRow label="Created" value={formatDateTime(evt.createdAt)} />
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Main Webhooks Page
// ============================================================================

const Webhooks: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('summary');

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Tab bar */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center gap-1 px-4 py-2 overflow-x-auto scrollbar-thin">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all whitespace-nowrap',
                  isActive
                    ? 'bg-primary/15 text-primary font-medium border border-primary/30'
                    : 'text-muted-foreground hover:bg-primary/10 hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        {activeTab === 'summary' && <SummaryTab />}
        {activeTab === 'events' && <AllEventsTab />}
        {activeTab === 'stripe' && <StripeTab />}
        {activeTab === 'checkr' && <CheckrTab />}
        {activeTab === 'bookings' && <BookingsTab />}
        {activeTab === 'sessions' && <SessionsTab />}
        {activeTab === 'safety' && <SafetyTab />}
        {activeTab === 'referrals' && <ReferralsTab />}
        {activeTab === 'call-logs' && <CallLogsTab />}
        {activeTab === 'analytics' && <AnalyticsTab />}
        {activeTab === 'audit-logs' && <AuditLogsTab />}
        {activeTab === 'uploads' && <UploadsTab />}
      </div>
    </div>
  );
};

export default Webhooks;
